import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  useAuth0ExchangeTokenQuery,
  useLazyGetUserInfoQuery,
} from '@/apis/auth/auth.api.ts'
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query'
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/utils/local-storage.ts'
import {
  requestUserInfo,
  setAccessToken,
  setUserInfo,
} from '@/features/user/user.slice.ts'
import { IdeyaFullScreenSpinner } from '@/common/components/ideya-spinner.tsx'
import {
  NEW_RELIC,
  NEW_RELIC_AUTH_EVENT,
  newRelic,
} from '@/utils/new-relic.ts'
import { getErrorDescription } from '@/redux/utils.ts'

export const Auth0LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [getUserInfo, userInfoResult] = useLazyGetUserInfoQuery()
  const {
    loginWithRedirect,
    isAuthenticated,
    isLoading,
    error,
    getAccessTokenSilently,
  } = useAuth0()
  const [userToken, setUserToken] = useState<string | null>(null)
  const dispatch = useDispatch()

  // Once Auth0 settles as unauthenticated, redirect to the Auth0 login page.
  useEffect(() => {
    if (!isLoading && (error || !isAuthenticated)) {
      // The SDK's own error is otherwise only used as a trigger to redirect —
      // its contents were never reported anywhere.
      if (error) {
        newRelic.noticeError(error, { context: 'auth0SdkError' })
      }
      loginWithRedirect()
    }
  }, [isLoading, error, isAuthenticated, loginWithRedirect])

  // Authenticated: pull the raw Auth0 token to exchange it for an app token.
  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently()
        .then((res) => {
          newRelic.recordCustomEvent(
            NEW_RELIC_AUTH_EVENT.auth0TokenAcquired,
            { element: NEW_RELIC.AUTH.auth0Token }
          )
          setUserToken(res)
        })
        .catch((err) => {
          newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.auth0TokenFailed, {
            element: NEW_RELIC.AUTH.auth0Token,
            event: `${err}`,
          })
          console.error('[Auth0LoginPage] getAccessTokenSilently error:', err)
          newRelic.noticeError(err as Error, {
            context: 'auth0GetAccessTokenSilently',
          })
        })
    }
  }, [isAuthenticated, getAccessTokenSilently])

  const {
    data: exchangeTokenData,
    isFetching,
    isSuccess,
    isError,
    error: exchangeError,
  } = useAuth0ExchangeTokenQuery(userToken ? userToken : skipToken)

  useEffect(() => {
    if (!isFetching && exchangeTokenData?.data.accessToken) {
      newRelic.recordCustomEvent(
        NEW_RELIC_AUTH_EVENT.exchangeTokenSucceeded,
        { element: NEW_RELIC.AUTH.exchangeToken }
      )
      setLocalStorageItem('apiAccessToken', exchangeTokenData.data.accessToken)
      dispatch(setAccessToken(exchangeTokenData.data.accessToken))
      dispatch(requestUserInfo())
      getUserInfo()
    }
  }, [exchangeTokenData, isFetching, dispatch, getUserInfo])

  useEffect(() => {
    if (userInfoResult.isError) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenFailed, {
        element: NEW_RELIC.AUTH.verifyToken,
        event: getErrorDescription(userInfoResult.error),
      })
      newRelic.noticeError(
        new Error(getErrorDescription(userInfoResult.error)),
        { context: 'auth0VerifyToken' }
      )
    }
  }, [userInfoResult.isError, userInfoResult.error])

  useEffect(() => {
    if (isSuccess && userInfoResult.isSuccess) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenSucceeded, {
        element: NEW_RELIC.AUTH.verifyToken,
      })
      dispatch(setUserInfo(userInfoResult.data.data))
      const disclaimerAccepted = getLocalStorageItem('disclaimerAccepted')
      // Empty role means an internal user, who skips the disclaimer page.
      if (!disclaimerAccepted && userInfoResult.data.data.role === '') {
        if (userInfoResult.data.data.region === 'Global') {
          navigate('/login/global-disclaimer', {
            state: { from: location.pathname },
          })
        } else {
          navigate('/login/disclaimer', {
            state: { from: location.pathname },
          })
          return
        }
      } else {
        navigate(location.state?.from || '/idea')
      }
    } else if (isError) {
      const errorResponse = exchangeError as FetchBaseQueryError
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.exchangeTokenFailed, {
        element: NEW_RELIC.AUTH.exchangeToken,
        event: getErrorDescription(exchangeError),
      })
      newRelic.noticeError(new Error(getErrorDescription(exchangeError)), {
        context: 'auth0ExchangeToken',
      })
      if ('status' in errorResponse && errorResponse.status === 403) {
        navigate('/inactive')
      } else {
        navigate('/logout')
      }
    }
  }, [
    isSuccess,
    userInfoResult.isSuccess,
    userInfoResult.data?.data,
    navigate,
    location.state?.from,
    dispatch,
    location.pathname,
    isError,
    exchangeError,
  ])

  return <IdeyaFullScreenSpinner />
}
